import { CashSessionSummary } from '@/@types/cash-management';
import { PrintLayout, PrintWidth } from '@/@types/print';
import {
  POS_PRINT_58MM_FONT_WEIGHT,
  POS_PRINT_BOLD_FONT_WEIGHT,
  POS_PRINT_FONT_FAMILY,
  POS_PRINT_58MM_FONT_SIZE,
  POS_PRINT_80MM_FONT_WEIGHT,
  POS_PRINT_RENDERING_STYLES,
  Separator,
} from '@/components/common/PosPrint';
import { COMPANY_CNPJ, COMPANY_NAME } from '@/constants';
import { PRINT_LAYOUT_1, PRINT_LAYOUT_2 } from '@/modules/utils/print-layouts';
import { ReactNode, Ref } from 'react';
import {
  cashSessionSummaryStatusLabel,
  formatSummaryCurrency,
  formatSummaryDateTime,
} from './CashSessionSummaryPrint.utils';

type CashSessionSummaryPosPrintProps = {
  summary?: CashSessionSummary;
  containerRef: Ref<HTMLDivElement>;
  selectedLayout?: PrintLayout;
  selectedWidth?: PrintWidth;
};

const POS_LINE_SIZE = 42;

function CashSessionPosHeader() {
  return (
    <div
      style={{
        textAlign: 'center',
        fontWeight: POS_PRINT_BOLD_FONT_WEIGHT,
      }}
    >
      <div>{COMPANY_NAME}</div>
      <div>{COMPANY_CNPJ}</div>
    </div>
  );
}

function CenteredTitle({ children }: { children: string }) {
  return (
    <div
      style={{
        textAlign: 'center',
        fontWeight: POS_PRINT_BOLD_FONT_WEIGHT,
      }}
    >
      {children}
    </div>
  );
}

function BodyRow({
  children,
  selectedLayout,
}: {
  children: ReactNode;
  selectedLayout: PrintLayout;
}) {
  return (
    <div
      style={{
        paddingLeft: selectedLayout === PRINT_LAYOUT_2 ? '0.25cm' : '0',
      }}
    >
      {children}
    </div>
  );
}

function ValueRow({
  label,
  value,
  selectedLayout,
  selectedWidth,
}: {
  label: string;
  value: string;
  selectedLayout: PrintLayout;
  selectedWidth: PrintWidth;
}) {
  const is58mm = selectedWidth === '58mm';
  const availableLabelSize = Math.max(POS_LINE_SIZE - value.length - 1, 8);
  let trimmedLabel = label;

  if (!is58mm && label.length > availableLabelSize) {
    trimmedLabel = label.slice(0, availableLabelSize - 1);
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 8,
        ...(is58mm ? { alignItems: 'flex-start' } : {}),
      }}
    >
      <span
        style={{
          paddingLeft: selectedLayout === PRINT_LAYOUT_2 ? '0.25cm' : '0',
          ...(is58mm
            ? { flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' }
            : {}),
        }}
      >
        {trimmedLabel}
      </span>
      <span style={is58mm ? { flexShrink: 0, whiteSpace: 'nowrap' } : {}}>
        {value}
      </span>
    </div>
  );
}

export function CashSessionSummaryPosPrint({
  summary,
  containerRef,
  selectedLayout = PRINT_LAYOUT_1,
  selectedWidth = '80mm',
}: CashSessionSummaryPosPrintProps) {
  if (!summary) return <div ref={containerRef} />;

  const { cashSession, summaries } = summary;
  const is58mm = selectedWidth === '58mm';

  return (
    <div
      ref={containerRef}
      style={{
        printColorAdjust: 'economy',
        width: selectedWidth === '58mm' ? '4.8cm' : '7.2cm',
        color: 'black',
        padding: '0.25cm',
        fontFamily: POS_PRINT_FONT_FAMILY,
        fontSize: is58mm ? POS_PRINT_58MM_FONT_SIZE : '11px',
        fontWeight: is58mm
          ? POS_PRINT_58MM_FONT_WEIGHT
          : POS_PRINT_80MM_FONT_WEIGHT,
        ...POS_PRINT_RENDERING_STYLES,
      }}
    >
      <CashSessionPosHeader />
      <Separator />
      <CenteredTitle>Movimento de Caixa</CenteredTitle>
      <Separator />
      <BodyRow selectedLayout={selectedLayout}>
        Numero: {cashSession.number}
      </BodyRow>
      <BodyRow selectedLayout={selectedLayout}>
        Usuario: {cashSession.user.name}
      </BodyRow>
      <BodyRow selectedLayout={selectedLayout}>
        Status: {cashSessionSummaryStatusLabel[cashSession.status]}
      </BodyRow>
      <BodyRow selectedLayout={selectedLayout}>
        Abertura: {formatSummaryDateTime(cashSession.openedAt)}
      </BodyRow>
      <Separator />
      <CenteredTitle>Creditos</CenteredTitle>
      <Separator />
      {summary.groups.credits.length ? (
        summary.groups.credits.map((group) => (
          <ValueRow
            key={group.documentType.id}
            label={`${group.items[0]?.defaultLabel || ''} ${
              group.documentType.name
            }`}
            value={formatSummaryCurrency(group.subtotal)}
            selectedLayout={selectedLayout}
            selectedWidth={selectedWidth}
          />
        ))
      ) : (
        <BodyRow selectedLayout={selectedLayout}>Nenhum.</BodyRow>
      )}
      <Separator />
      <CenteredTitle>A Prazo / Resgates (Informativo)</CenteredTitle>
      <Separator />
      {summaries.futureCredits.length ? (
        summaries.futureCredits.map((item) => (
          <ValueRow
            key={item.documentType.id}
            label={item.documentType.name}
            value={formatSummaryCurrency(item.amount)}
            selectedLayout={selectedLayout}
            selectedWidth={selectedWidth}
          />
        ))
      ) : (
        <BodyRow selectedLayout={selectedLayout}>Nenhum.</BodyRow>
      )}
      <Separator />
      <CenteredTitle>Debitos</CenteredTitle>
      <Separator />
      {summary.groups.debits.length ? (
        summary.groups.debits.map((group) => (
          <ValueRow
            key={group.documentType.id}
            label={`${group.items[0]?.defaultLabel || ''} ${
              group.documentType.name
            }`}
            value={formatSummaryCurrency(group.subtotal)}
            selectedLayout={selectedLayout}
            selectedWidth={selectedWidth}
          />
        ))
      ) : (
        <BodyRow selectedLayout={selectedLayout}>Nenhum.</BodyRow>
      )}
      <Separator />
      <CenteredTitle>Totais em Dinheiro</CenteredTitle>
      <Separator />
      <ValueRow
        label="(+) Saldo Inicial"
        value={formatSummaryCurrency(summaries.cashTotals.startingCash)}
        selectedLayout={selectedLayout}
        selectedWidth={selectedWidth}
      />
      <ValueRow
        label="(+) Vendas"
        value={formatSummaryCurrency(summaries.cashTotals.cashSales)}
        selectedLayout={selectedLayout}
        selectedWidth={selectedWidth}
      />
      <ValueRow
        label="(+) Recebimentos"
        value={formatSummaryCurrency(summaries.cashTotals.cashPayIns)}
        selectedLayout={selectedLayout}
        selectedWidth={selectedWidth}
      />
      <ValueRow
        label="(-) Total Debitos"
        value={formatSummaryCurrency(summaries.cashTotals.totalDebits)}
        selectedLayout={selectedLayout}
        selectedWidth={selectedWidth}
      />
      <Separator />
      <ValueRow
        label="(=) Saldo Total"
        value={formatSummaryCurrency(summaries.cashTotals.finalBalance)}
        selectedLayout={selectedLayout}
        selectedWidth={selectedWidth}
      />
      <ValueRow
        label="(-) Saldo Inicial"
        value={formatSummaryCurrency(summaries.cashTotals.startingCash)}
        selectedLayout={selectedLayout}
        selectedWidth={selectedWidth}
      />
      <Separator />
      <ValueRow
        label="(=) Saldo Final"
        value={formatSummaryCurrency(
          summaries.cashTotals.finalBalance - summaries.cashTotals.startingCash
        )}
        selectedLayout={selectedLayout}
        selectedWidth={selectedWidth}
      />
      <Separator />
    </div>
  );
}
